import React, { useEffect, useMemo, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function Job({ partNumArr, licenseValid, company }) {
  const [showForm, setShowForm] = useState(false);
  const [mode, setMode] = useState("newOrder");
  const [customers, setCustomers] = useState([]);
  const [custNum, setCustNum] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [existingOrderNum, setExistingOrderNum] = useState("");
  const [plant, setPlant] = useState("");
  const [plants, setPlants] = useState([]);
  const [creating, setCreating] = useState(false);

  const devBaseUrl = process.env.REACT_APP_DEV_URI;
  const selectedPlant = String(plant).split(" - ")[0].trim();

  const getAuthHeaders = () => {
    const token = btoa(
      `${process.env.REACT_APP_USER}:${process.env.REACT_APP_PASSWORD}`,
    );

    return {
      Authorization: `Basic ${token}`,
      "Content-Type": "application/json",
    };
  };

  const jobGroups = useMemo(() => {
    const rows = (partNumArr || []).map((row) => {
      const partNum = Array.isArray(row)
        ? row[0]
        : row?.partNum || row?.PartNum || row?.partLot_BuildingType_c;

      const elementId = Array.isArray(row)
        ? row[1]
        : row?.partLot_LotNum || row?.LotNum || row?.elementId;

      const count = Array.isArray(row)
        ? Number(row[2]) || 1
        : Number(row?.partLot_Count_c) || Number(row?.count) || 1;

      const length = Array.isArray(row)
        ? Number(row[3]) || 0
        : Number(row?.partLot_Length_c) || Number(row?.length) || 0;

      const width = Array.isArray(row)
        ? Number(row[4]) || 0
        : Number(row?.partLot_Width_c) || Number(row?.width) || 0;

      const depth = Array.isArray(row)
        ? Number(row[5]) || 0
        : Number(row?.partLot_Depth_c) || Number(row?.depth) || 0;

      if (!partNum) return null;

      return {
        partNum,
        elementId,
        qty: count,
        count,
        length,
        width,
        depth,
        elementIds: elementId ? [elementId] : [],
      };
    });

    return rows.filter(Boolean);
  }, [partNumArr]);

  useEffect(() => {
    const fetchCustomers = async () => {
      if (!company) return;

      try {
        const response = await fetch(
          `${devBaseUrl}/v1/BaqSvc/IIT_AAC_Customer(${company})/`,
          { headers: getAuthHeaders() },
        );

        const data = await response.json();
        setCustomers(data.value || []);
      } catch (error) {
        console.error("Error fetching customers:", error);
        toast.error("Error fetching customers");
      }
    };

    const fetchPlants = async () => {
      if (!company) return;

      try {
        const response = await axios.get(
          `${devBaseUrl}/v1/Erp.BO.PlantSvc/Plants`,
          { headers: getAuthHeaders() },
        );

        const plantList = response.data.value || [];
        setPlants(plantList);

        if (plantList.length > 0) {
          setPlant(plantList[0].Plant1 || plantList[0].Plant);
        }
      } catch (error) {
        console.error("Error fetching plants:", error);
        toast.error("Error loading sites");
      }
    };

    fetchCustomers();
    fetchPlants();
  }, [company, devBaseUrl]);

  const fetchPartDescription = async (partNum) => {
    try {
      const response = await axios.get(
        `${devBaseUrl}/v1/Erp.BO.PartSvc/Parts?$filter=PartNum eq '${partNum}'`,
        { headers: getAuthHeaders() },
      );

      return response.data.value?.[0]?.PartDescription || partNum;
    } catch {
      return partNum;
    }
  };

  const fetchPartRevision = async (partNum) => {
    const safePartNum = String(partNum).replace(/'/g, "''");

    try {
      const response = await axios.get(
        `${devBaseUrl}/v1/Erp.BO.PartRevSearchSvc/PartRevSearches?$filter=PartNum eq '${safePartNum}'`,
        { headers: getAuthHeaders() },
      );

      const revisions = response.data.value || [];

      if (!revisions.length) {
        console.warn(`No revision found for part ${partNum}`);
        return "";
      }

      const approvedRevision =
        revisions.find((rev) => rev.Approved === true) ||
        revisions.find((rev) => rev.Approved === "true") ||
        revisions[0];

      return approvedRevision.RevisionNum || "";
    } catch (error) {
      console.warn(
        `Revision lookup failed for part ${partNum}. Continuing without revision.`,
        error?.response?.data || error,
      );

      return "";
    }
  };

  const isValidGuid = (value) => {
    if (typeof value !== "string" || !value) return false;

    return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
      value,
    );
  };

  const generateJobNum = (partNum, index) => {
    const cleanPart = String(partNum || "JOB")
      .replace(/[^A-Za-z0-9]/g, "")
      .toUpperCase()
      .slice(0, 3);

    const timestamp = Date.now().toString().slice(-8);
    const seq = String(index + 1).padStart(2, "0");

    return `${cleanPart}${timestamp}${seq}`.slice(0, 14);
  };

  const createSalesOrder = async () => {
    const today = new Date().toISOString();
    const requestDate = dueDate ? `${dueDate}T00:00:00` : today;

    const response = await axios.post(
      `${devBaseUrl}/v1/Erp.BO.SalesOrderSvc/SalesOrders`,
      {
        Company: company,
        CustNum: Number(custNum),
        OpenOrder: true,
        VoidOrder: false,
        OrderHeld: false,
        OrderDate: today,
        RequestDate: requestDate,
        NeedByDate: requestDate,
        RowMod: "A",
      },
      { headers: getAuthHeaders() },
    );

    const orderNum = response.data.OrderNum;

    if (!orderNum) {
      throw new Error("Sales order was created but OrderNum was not returned");
    }

    return orderNum;
  };

  const createOrderLine = async ({ orderNum, group, orderLine }) => {
    const partDescription = await fetchPartDescription(group.partNum);
    const requestedQty = Number(group.qty) || 1;

    const lineResponse = await axios.post(
      `${devBaseUrl}/v1/Erp.BO.SalesOrderSvc/OrderDtls`,
      {
        Company: company.toUpperCase(),
        OrderNum: Number(orderNum),
        OrderLine: Number(orderLine),
        PartNum: group.partNum,
        CustNum: Number(custNum),
        OrderQty: String(requestedQty),
        SellingQuantity: String(requestedQty),
      },
      { headers: getAuthHeaders() },
    );

    let sysRowID = lineResponse.data.SysRowID || "";

    if (!isValidGuid(sysRowID)) {
      const dtl = await getOrderDtlById({ orderNum, orderLine });
      sysRowID = dtl?.SysRowID || "";
    }

    return {
      orderNum,
      orderLine: lineResponse.data.OrderLine || orderLine,
      sysRowID,
      orderRelNum: 1,
      partNum: group.partNum,
      qty: group.qty,
      partDescription,
      elementIds: group.elementIds,
    };
  };

  const patchOrderReleaseMakeDirect = async ({
    orderNum,
    orderLine,
    partNum,
    qty,
  }) => {
    const orderRelNum = 1;

    const response = await axios.patch(
      `${devBaseUrl}/v1/Erp.BO.SalesOrderSvc/OrderRels('${company.toUpperCase()}',${Number(
        orderNum,
      )},${Number(orderLine)},${orderRelNum})`,
      {
        Company: company.toUpperCase(),
        OrderNum: Number(orderNum),
        OrderLine: Number(orderLine),
        OrderRelNum: orderRelNum,
        PartNum: partNum,
        Make: true,
        RowMod: "U",
      },
      { headers: getAuthHeaders() },
    );

    let sysRowID = response.data?.SysRowID || "";

    if (!isValidGuid(sysRowID)) {
      const relResponse = await axios.get(
        `${devBaseUrl}/v1/Erp.BO.SalesOrderSvc/OrderRels('${company.toUpperCase()}',${Number(
          orderNum,
        )},${Number(orderLine)},${orderRelNum})`,
        { headers: getAuthHeaders() },
      );
      sysRowID = relResponse.data?.SysRowID || "";
    }

    return { orderRelNum, sysRowID };
  };

  const createNewOrderLinks = async () => {
    const orderNum = await createSalesOrder();
    const orderLinks = [];

    for (let i = 0; i < jobGroups.length; i++) {
      const group = jobGroups[i];

      const link = await createOrderLine({
        orderNum,
        group,
        orderLine: i + 1,
      });

      const { orderRelNum, sysRowID: orderRelSysRowID } =
        await patchOrderReleaseMakeDirect({
          orderNum,
          orderLine: link.orderLine,
          partNum: link.partNum,
          qty: link.qty,
        });

      orderLinks.push({
        ...link,
        orderRelNum,
      });
    }

    return { orderNum, orderLinks };
  };

  const getOrderDtlById = async ({ orderNum, orderLine }) => {
    const response = await axios.get(
      `${devBaseUrl}/v1/Erp.BO.SalesOrderSvc/OrderDtls('${company.toUpperCase()}',${Number(
        orderNum,
      )},${Number(orderLine)})`,
      { headers: getAuthHeaders() },
    );

    return response.data;
  };

  const getLinksFromExistingOrder = async () => {
    const orderNum = Number(existingOrderNum);
    const orderLinks = [];

    for (let i = 0; i < jobGroups.length; i++) {
      const group = jobGroups[i];
      const orderLine = i + 1;

      const existingLine = await getOrderDtlById({
        orderNum,
        orderLine,
      });

      if (!existingLine) {
        throw new Error(
          `Order line ${orderLine} was not found in existing order ${orderNum}`,
        );
      }

      if (
        String(existingLine.PartNum).toUpperCase() !==
        String(group.partNum).toUpperCase()
      ) {
        throw new Error(
          `Order ${orderNum}, line ${orderLine} has part ${existingLine.PartNum}, but uploaded part is ${group.partNum}`,
        );
      }

      const { orderRelNum, sysRowID: orderRelSysRowID } =
        await patchOrderReleaseMakeDirect({
          orderNum,
          orderLine,
          partNum: group.partNum,
          qty: group.qty,
        });

      const partDescription =
        existingLine.LineDesc || (await fetchPartDescription(group.partNum));

      orderLinks.push({
        orderNum,
        orderLine,
        orderRelNum,
        partNum: group.partNum,
        qty: group.qty,
        partDescription,
        elementIds: group.elementIds,
      });
    }

    return { orderNum, orderLinks };
  };

  const createJobEntry = async (link, index) => {
    const selectedDueDate = dueDate
      ? `${dueDate}T00:00:00`
      : new Date().toISOString();

    const jobNum = generateJobNum(link.partNum, index);
    const revisionNum = await fetchPartRevision(link.partNum);

    const jobEntryPayload = {
      Company: company,
      JobNum: jobNum,
      PartNum: link.partNum,
      PartDescription: link.partDescription,
      // IUM: "EA",
      Plant: selectedPlant,
      ReqDueDate: selectedDueDate,
      DueDate: selectedDueDate,
      JobFirm: true,
      JobReleased: false,
      JobHeld: false,
      JobClosed: false,
      JobComplete: false,
      JobEngineered: false,
      JobType: "MFG",
      CommentText: `Created from order ${link.orderNum}, line ${link.orderLine}`,
      // Character01: link.elementIds.join(","),
      RowMod: "A",
    };

    if (revisionNum) {
      jobEntryPayload.RevisionNum = revisionNum;
    }

    const response = await axios.post(
      `${devBaseUrl}/v1/Erp.BO.JobEntrySvc/JobEntries`,
      jobEntryPayload,
      { headers: getAuthHeaders() },
    );

    return {
      jobNum,
      revisionNum,
      sysRowID: response.data?.SysRowID || "",
    };
  };

  const getDetailsForJob = async ({ jobNum, partNum, revisionNum }) => {
    const sourceFile = revisionNum ? "Method" : "PART";

    const detailsResponse = await axios.post(
      `${devBaseUrl}/v1/Erp.BO.JobEntrySvc/GetDetailsMsgWarning`,
      {
        currJobNum: jobNum,
        currAsmSeq: 0,
        sourceFile: sourceFile,
        sourceQuote: 0,
        sourceLine: 0,
        sourceJob: "",
        sourceAsm: 0,
        sourcePart: partNum,
        sourceRev: revisionNum || "",
        sourceAltMethod: "",
        resequence: false,
        useMethodForParts: false,
        getCostsFromInv: false,
        getCostsFromTemp: false,
      },
      { headers: getAuthHeaders() },
    );

    const ds = detailsResponse.data?.returnObj;

    if (ds) {
      await axios.post(
        `${devBaseUrl}/v1/Erp.BO.JobEntrySvc/Update`,
        { ds },
        { headers: getAuthHeaders() },
      );
    }

    return detailsResponse.data;
  };

  const createJobProd = async (link, jobNum) => {
    await axios.post(
      `${devBaseUrl}/v1/Erp.BO.JobEntrySvc/JobProds`,
      {
        Company: company,
        JobNum: jobNum,
        OrderNum: Number(link.orderNum),
        OrderLine: Number(link.orderLine),
        OrderRelNum: Number(link.orderRelNum),
        PartNum: link.partNum,
        MakeToType: "ORDER",
      },
      { headers: getAuthHeaders() },
    );
  };

  const createJobForOrderLine = async (link, index) => {
    const { jobNum, revisionNum, sysRowID } = await createJobEntry(link, index);

    await getDetailsForJob({
      jobNum,
      partNum: link.partNum,
      revisionNum,
    });

    await createJobProd(link, jobNum);

    return {
      jobNum,
      partNum: link.partNum,
      qty: link.qty,
      orderNum: link.orderNum,
      orderLine: link.orderLine,
      orderRelNum: link.orderRelNum,
      plant: selectedPlant,
      revisionNum,
      sysRowID,
    };
  };

  const createOrderAndJobs = async () => {
    if (!company) {
      toast.error("Company is missing");
      return;
    }

    if (!selectedPlant) {
      toast.error("Please select site");
      return;
    }

    if (mode === "newOrder" && !custNum) {
      toast.error("Please select customer");
      return;
    }

    if (mode === "existingOrder" && !existingOrderNum) {
      toast.error("Please enter existing order number");
      return;
    }

    if (!jobGroups.length) {
      toast.error("No uploaded element parts found");
      return;
    }

    setCreating(true);

    try {
      const { orderNum, orderLinks } =
        mode === "newOrder"
          ? await createNewOrderLinks()
          : await getLinksFromExistingOrder();

      const createdJobs = [];

      for (let i = 0; i < orderLinks.length; i++) {
        const createdJob = await createJobForOrderLine(orderLinks[i], i);
        createdJobs.push(createdJob);
      }

      toast.success(
        `Order ${orderNum} linked and ${createdJobs.length} jobs created successfully`,
      );

      console.log("Order:", orderNum);
      console.log("Created Jobs:", createdJobs);
    } catch (error) {
      console.error("Error creating order/jobs:", error);
      console.error("Epicor error body:", error?.response?.data);

      toast.error(
        error?.response?.data?.ErrorMessage ||
          error?.response?.data?.message ||
          error?.response?.data?.Message ||
          JSON.stringify(error?.response?.data) ||
          error?.message ||
          "Error creating order/jobs",
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="Form-Panel m-1 px-2 py-2">
      <div className="title-border-bottom">
        <button
          className="toggle-btn my-3 mx-5"
          onClick={() => setShowForm(!showForm)}
          style={{
            cursor: licenseValid ? "pointer" : "not-allowed",
            color: licenseValid ? "black" : "gray",
          }}
          disabled={!licenseValid}
        >
          <i
            className="bi bi-arrow-down-short"
            style={{
              fontSize: "2rem",
              fontWeight: "bolder",
              color: licenseValid ? "black" : "gray",
            }}
          ></i>
          Job Details
        </button>
      </div>

      {showForm && (
        <div className="mb-2">
          <form className="p-2 m-2 form-bg border rounded">
            <div className="container">
              <div className="row">
                <div className="col-md-4">
                  <label>Flow</label>
                  <select
                    className="form-control"
                    value={mode}
                    onChange={(e) => setMode(e.target.value)}
                  >
                    <option value="newOrder">Create New Order and Jobs</option>
                    <option value="existingOrder">
                      Create Jobs from Existing Order
                    </option>
                  </select>
                </div>

                <div className="col-md-4">
                  <label>Site</label>
                  <select
                    className="form-control"
                    value={plant}
                    onChange={(e) => setPlant(e.target.value)}
                  >
                    <option value="">Select Site</option>
                    {plants.map((p) => (
                      <option
                        key={p.Plant1 || p.Plant}
                        value={p.Plant1 || p.Plant}
                      >
                        {p.Plant1 || p.Plant} {p.Name ? `- ${p.Name}` : ""}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4">
                  <label>Due Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="row mt-3">
                {mode === "newOrder" && (
                  <div className="col-md-4">
                    <label>Customer</label>
                    <select
                      className="form-control"
                      value={custNum}
                      onChange={(e) => setCustNum(e.target.value)}
                    >
                      <option value="">Select Customer</option>
                      {customers.map((customer) => (
                        <option
                          key={customer.Customer_CustNum}
                          value={customer.Customer_CustNum}
                        >
                          {customer.Customer_Name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {mode === "existingOrder" && (
                  <div className="col-md-4">
                    <label>Existing Order Number</label>
                    <input
                      type="number"
                      className="form-control"
                      value={existingOrderNum}
                      onChange={(e) => setExistingOrderNum(e.target.value)}
                      placeholder="Order Number"
                    />
                  </div>
                )}
              </div>

              <div className="my-3">
                <strong>Jobs to create:</strong>
                {jobGroups.length === 0 ? (
                  <p>No uploaded element parts found.</p>
                ) : (
                  <ul>
                    {jobGroups.map((group, index) => (
                      <li key={index}>
                        {group.partNum}: {group.count} pcs, {group.length} x{" "}
                        {group.width} x {group.depth} mm
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <button
                type="button"
                className="submit-btn ro d-block m-2 rounded"
                onClick={createOrderAndJobs}
                disabled={creating || !licenseValid}
              >
                {creating ? "Creating..." : "Create Jobs"}
              </button>
            </div>
          </form>
        </div>
      )}

      <ToastContainer />
    </div>
  );
}

export default Job;
